import { io } from 'socket.io-client';
import notificationPreferences from '../services/notificationPreferences';

// Enhanced browser notification helper that checks preferences
async function showBrowserNotification(title, options = {}, type = 'general') {
  if (typeof window === 'undefined') return;
  if (!('Notification' in window)) return;
  
  // Check user preferences first
  const canShow = await notificationPreferences.canShowBrowserNotification(type);
  if (!canShow) {
    console.log('Browser notification blocked by user preferences:', type);
    return;
  }
  
  if (Notification.permission === 'granted') {
    try { 
      new Notification(title, options); 
    } catch (e) {
      console.error('Failed to show notification:', e);
    }
  } else if (Notification.permission === 'default') {
    Notification.requestPermission().then(result => {
      if (result === 'granted') {
        try { 
          new Notification(title, options); 
        } catch (e) {
          console.error('Failed to show notification:', e);
        }
      }
    }).catch(e => {
      console.error('Failed to request notification permission:', e);
    });
  }
}

class SocketClient {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.isAuthenticated = false;
    this.currentRoom = null;
  this.currentMode = 'participant';
    this.eventListeners = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.pendingToken = null;
    this.lastError = null;
    // Buffer for listeners registered before actual socket initialization
    this.bufferedListeners = [];
    // Prevent multiple auto-connect attempts
    this.autoConnectAttempted = false;
    this.authenticatedUserId = null;
  this.receivedChatMessageIds = new Set();
  this.receivedChatMessageOrder = [];
  this.maxCachedChatMessages = 200;
  }

  // Determine best socket URL (env override > api url root > window origin)
  resolveSocketUrl() {
    if (process.env.REACT_APP_SOCKET_URL) return process.env.REACT_APP_SOCKET_URL;
    const api = process.env.REACT_APP_API_URL;
    if (api) {
      try {
        const url = new URL(api);
        url.pathname = url.pathname.replace(/\/api\/?$/, '/');
        const socketUrl = url.toString().replace(/\/$/, '');
        console.log('🔌 Resolved socket URL from API_URL:', socketUrl);
        return socketUrl;
      } catch (e) {
        console.warn('⚠️ Failed to parse REACT_APP_API_URL:', api, e);
      }
    }
    if (typeof window !== 'undefined') {
      const origin = window.location.origin.replace(/\/$/, '');
      console.log('🔌 Using window origin for socket:', origin);
      return origin;
    }
    return 'http://localhost:5000';
  }

  // Initialize socket connection
  connect(token = null) {
    if (this.socket && this.isConnected) {
      console.log('Socket already connected');
      return;
    }

  const socketUrl = this.resolveSocketUrl();
  console.log('🔌 Connecting to socket server:', socketUrl, 'tokenProvided=', !!token);
    
    this.socket = io(socketUrl, {
      transports: ['websocket', 'polling'],
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: this.reconnectDelay,
      timeout: 20000,
      withCredentials: true
    });

    this.setupEventListeners();
    // Flush any buffered listeners registered prior to connect
    if (this.bufferedListeners.length > 0) {
      this.bufferedListeners.forEach(({ event, callback }) => {
        try { this.socket.on(event, callback); } catch (e) { console.error('Failed to attach buffered listener', event, e); }
      });
      this.bufferedListeners = [];
    }
    
    // Store token for authentication after connection
    if (token) {
      this.pendingToken = token;
    }
  }

  // Setup basic event listeners
  setupEventListeners() {
    this.socket.on('connect', () => {
      console.log('🔌 Socket connected:', this.socket.id);
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this.emit('socket_connected', { socketId: this.socket.id });
      
      // Authenticate with pending token if available
      if (this.pendingToken) {
        console.log('🔐 Authenticating with pending token...');
        this.authenticate(this.pendingToken);
        this.pendingToken = null;
      }
    });

    this.socket.on('disconnect', (reason) => {
      console.log('🔌 Socket disconnected:', reason);
      this.isConnected = false;
      this.isAuthenticated = false;
      this.currentRoom = null;
      this.authenticatedUserId = null;
      this.receivedChatMessageIds.clear();
      this.receivedChatMessageOrder = [];
      this.emit('socket_disconnected', { reason });
    });

    this.socket.on('connect_error', (error) => {
      console.error('🔌 Socket connection error:', error);
      console.error('🔌 Error details:', {
        message: error.message,
        description: error.description,
        context: error.context,
        type: error.type
      });
      this.lastError = {
        at: new Date().toISOString(),
        message: error.message,
        description: error.description,
        context: error.context,
        type: error.type,
        attemptedUrl: this.socket?.io?.uri
      };
      this.reconnectAttempts++;
      this.emit('socket_connect_error', { error, attempts: this.reconnectAttempts });
    });

    this.socket.on('reconnect', (attemptNumber) => {
      console.log('🔌 Socket reconnected after', attemptNumber, 'attempts');
      this.isConnected = true;
      this.emit('socket_reconnected', { attemptNumber });
    });

    this.socket.on('reconnect_error', (error) => {
      console.error('🔌 Socket reconnection error:', error);
      this.emit('socket_reconnect_error', { error });
    });

    this.socket.on('reconnect_failed', () => {
      console.error('🔌 Socket reconnection failed');
      this.emit('socket_reconnect_failed');
    });

    // Debug low-level transport errors
    this.socket.io.engine.on?.('error', (err) => {
      console.error('🛑 Engine error:', err?.message || err);
      this.lastError = { at: new Date().toISOString(), engine: true, message: err?.message };
    });

    // Listen for authentication events from server
    this.socket.on('auth_success', (data) => {
      console.log('✅ Socket authenticated:', data.user.username);
      this.isAuthenticated = true;
      this.authenticatedUserId = data?.user?._id?.toString?.() || data?.user?._id || data?.user?.id || null;
      this.receivedChatMessageIds.clear();
      this.receivedChatMessageOrder = [];
      this.emit('socket_authenticated', data);
    });

    this.socket.on('auth_error', (data) => {
      console.error('❌ Socket authentication failed:', data.message);
      this.isAuthenticated = false;
      this.emit('socket_auth_error', data);
    });

    // Listen for validation errors from server
    this.socket.on('validation_error', (data) => {
      console.error('❌ Validation error:', data.message);
      this.emit('validation_error', data);
    });

    // Listen for offer events
    this.socket.on('offer_made', (data) => {
      console.log('📝 New offer made:', data);
      this.emit('offer_made', data);
    });

    this.socket.on('offer_accepted', (data) => {
      console.log('✅ Offer accepted:', data);
      this.emit('offer_accepted', data);
    });

    this.socket.on('offer_rejected', (data) => {
      console.log('❌ Offer rejected:', data);
      this.emit('offer_rejected', data);
    });

    this.socket.on('offer_withdrawn', (data) => {
      console.log('🚫 Offer withdrawn:', data);
      this.emit('offer_withdrawn', data);
    });

    this.socket.on('new_offer_notification', (data) => {
      console.log('🔔 New offer notification:', data);
      this.emit('new_offer_notification', data);
    });

    this.socket.on('fee_payment_required', (data) => {
      console.log('💰 Fee payment required:', data);
      this.emit('fee_payment_required', data);
    });

    this.socket.on('fee_paid', (data) => {
      console.log('💰 Fee paid:', data);
      this.emit('fee_paid', data);
    });

    this.socket.on('chat_started', (data) => {
      console.log('💬 Chat started:', data);
      this.emit('chat_started', data);
    });

    this.socket.on('messages_read', (data) => {
      console.log('👁️ Messages read:', data);
      this.emit('messages_read', data);
    });

    this.socket.on('chat_started', (data) => {
      console.log('💬 Chat started:', data);
      this.emit('chat_started', data);
    });

    this.socket.on('chat_message', (data) => {
      console.log('💬 Chat message:', data);
      const message = data?.message || {};

      const resolveId = (value) => {
        if (!value) return null;
        if (typeof value === 'string') return value;
        if (typeof value === 'object') {
          if (value.$oid) return value.$oid;
          if (value._id) return resolveId(value._id);
          if (typeof value.toString === 'function' && value.toString !== Object.prototype.toString) {
            return value.toString();
          }
        }
        return String(value);
      };

      const messageKey = resolveId(data?.messageKey) || resolveId(message._id) || resolveId(message.id);
      const cacheKey = messageKey || `${resolveId(message.senderId) || 'unknown'}-${message.timestamp || message.createdAt || Date.now()}-${message.content || ''}`;

      if (this.receivedChatMessageIds.has(cacheKey)) {
        return;
      }

      this.receivedChatMessageIds.add(cacheKey);
      this.receivedChatMessageOrder.push(cacheKey);
      if (this.receivedChatMessageOrder.length > this.maxCachedChatMessages) {
        const removed = this.receivedChatMessageOrder.shift();
        if (removed) {
          this.receivedChatMessageIds.delete(removed);
        }
      }

      this.emit('chat_message', data);
      // Desktop notification for chat
      const content = data?.message?.content || data?.content;
      const sender = message?.senderId;
      const senderId = resolveId(sender);
      const authId = resolveId(this.authenticatedUserId);
      const isOwnMessage = authId && senderId && senderId.toString() === authId.toString();

      if (content && !isOwnMessage) {
        showBrowserNotification('New Chat Message', {
          body: content.slice(0, 120),
          icon: '/logo192.png'
        }, 'message');
      }
    });

    // Permissions updated event (token refresh handled elsewhere)
    this.socket.on('permissions_updated', (data) => {
      console.log('🔄 Permissions updated event (socketClient):', data);
      this.emit('permissions_updated', data);
      showBrowserNotification('Permissions Updated', { 
        body: 'Your access levels were changed.',
        icon: '/logo192.png'
      }, 'general');
    });

    // General notification push from backend
    this.socket.on('notification_new', (data) => {
      console.log('🔔 Real-time notification:', data);
      this.emit('notification_new', data);
      
      // Use specific notification type from data or fallback to general
      const notificationType = data?.type || 'general';
      showBrowserNotification(
        this.getNotificationTitle(data), 
        { 
          body: data?.message,
          icon: '/logo192.png',
          tag: `notification-${data?.id || Date.now()}`
        }, 
        notificationType
      );
    });

    // Order status updates
    this.socket.on('order:status', (data) => {
      console.log('📦 Order status update (socket):', data);
      this.emit('order_status', data);

      // Show browser notification for status changes
      const orderId = data?.orderId || data?._id;
      const status = data?.status || (data?.order && data.order.status);
      const message = `Order ${orderId} status: ${status}`;
      showBrowserNotification('Order Update', { body: message, icon: '/logo192.png' }, 'general');
    });

    this.socket.on('ping_response', (data) => {
      this.emit('ping_response', data);
    });
  }

  // Authenticate socket connection
  authenticate(token) {
    if (!this.socket || !this.isConnected) {
      console.error('Socket not connected, storing token for later authentication');
      this.pendingToken = token;
      return;
    }

    console.log('🔐 Sending authentication request...');
    this.socket.emit('authenticate', { token });
  }

  // Join auction room
  joinRoom(roomId, mode = 'participant') {
    if (!this.socket || !this.isConnected || !this.isAuthenticated) {
      console.error('Socket not ready for joining room');
      return;
    }

    const normalizedMode = mode === 'observer' ? 'observer' : 'participant';
    this.socket.emit('join_room', { roomId, mode: normalizedMode });
    this.currentRoom = roomId;
    this.currentMode = normalizedMode;
  }

  // Leave auction room
  leaveRoom(roomId = null, mode = null) {
    if (!this.socket || !this.isConnected) {
      return;
    }

    const roomToLeave = roomId || this.currentRoom;
    if (roomToLeave) {
      const normalizedMode = mode === 'observer' ? 'observer' : (mode ? 'participant' : this.currentMode);
      this.socket.emit('leave_room', { roomId: roomToLeave, mode: normalizedMode });
      this.currentRoom = null;
      this.currentMode = 'participant';
    }
  }

  // Place bid
  placeBid(amount) {
    if (!this.socket || !this.isConnected || !this.isAuthenticated || !this.currentRoom) {
      console.error('Socket not ready for placing bid');
      return;
    }

    this.socket.emit('place_bid', {
      amount
    });
  }

  // Measure latency to server
  sendPing() {
    if (!this.socket || !this.isConnected) {
      return null;
    }

    const clientSentAt = Date.now();
    this.socket.emit('ping_check', { clientSentAt });
    return clientSentAt;
  }

  // Listen to specific events
  on(event, callback) {
    if (!this.socket) {
      // Attempt auto-connect once if token exists (user may not yet be in AuthProvider lifecycle)
      if (!this.autoConnectAttempted) {
        this.autoConnectAttempted = true;
        try {
          const token = localStorage.getItem('token') || localStorage.getItem('authToken') || localStorage.getItem('accessToken');
          if (token) {
            console.log('[socketClient] Auto-connecting due to early event subscription');
            this.connect(token);
          }
        } catch (e) {}
      }
      // Buffer the listener so it is attached after connect
      this.bufferedListeners.push({ event, callback });
      console.warn(`Socket not initialized yet. Buffering listener for '${event}'.`);
      return;
    }

    // Store callback for cleanup
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
    this.eventListeners.get(event).push(callback);

    this.socket.on(event, callback);
  }

  // Remove event listener
  off(event, callback = null) {
    if (!this.socket) {
      return;
    }

    if (callback) {
      this.socket.off(event, callback);
      const listeners = this.eventListeners.get(event);
      if (listeners) {
        const index = listeners.indexOf(callback);
        if (index > -1) {
          listeners.splice(index, 1);
        }
      }
    } else {
      this.socket.off(event);
      this.eventListeners.delete(event);
    }
  }

  // Emit custom events for internal use
  emit(event, data) {
    // This is for internal event emission (not socket events)
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error('Error in event listener:', error);
        }
      });
    }
  }

  // Get appropriate title for notification type
  getNotificationTitle(data) {
    const type = data?.type;
    switch (type) {
      case 'bid_placed':
        return '💰 New Bid Placed';
      case 'outbid':
        return '⚠️ You\'ve been outbid!';
      case 'auction_ended':
        return '🏁 Auction Ended';
      case 'new_offer':
        return '📝 New Offer Received';
      case 'offer_accepted':
        return '✅ Offer Accepted';
      case 'offer_rejected':
        return '❌ Offer Rejected';
      case 'message':
        return '💬 New Message';
      case 'payment_received':
        return '💳 Payment Received';
      case 'item_shipped':
        return '📦 Item Shipped';
      default:
        return '🔔 Notification';
    }
  }

  // Get connection status
  getStatus() {
    return {
      isConnected: this.isConnected,
      isAuthenticated: this.isAuthenticated,
      currentRoom: this.currentRoom,
      socketId: this.socket?.id,
      lastError: this.lastError
    };
  }

  // Disconnect socket
  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.isAuthenticated = false;
      this.currentRoom = null;
      this.currentMode = 'participant';
      this.eventListeners.clear();
      this.bufferedListeners = [];
    }
  }

  // Cleanup all listeners
  cleanup() {
    if (this.socket) {
      this.socket.removeAllListeners();
    }
    this.eventListeners.clear();
  }

  // Offer-related methods
  joinOfferRoom(productId) {
    if (!this.socket || !this.isConnected || !this.isAuthenticated) {
      console.error('Socket not ready for joining offer room');
      return;
    }

    this.socket.emit('join_offer_room', { productId });
    console.log(`Joined offer room for product ${productId}`);
  }

  leaveOfferRoom(productId) {
    if (!this.socket || !this.isConnected) {
      return;
    }

    this.socket.emit('leave_offer_room', { productId });
    console.log(`Left offer room for product ${productId}`);
  }

  sendChatMessage(receiverId, productId, content) {
    if (!this.socket || !this.isConnected || !this.isAuthenticated) {
      console.error('Socket not ready for sending chat message');
      return;
    }

    this.socket.emit('send_chat_message', {
      receiverId,
      productId,
      content
    });
  }

  markMessagesAsRead(productId, partnerId) {
    if (!this.socket || !this.isConnected || !this.isAuthenticated) {
      console.error('Socket not ready for marking messages as read');
      return;
    }

    this.socket.emit('mark_messages_read', {
      productId,
      partnerId
    });
  }
}

// Create singleton instance
const socketClient = new SocketClient();

// Named helper accessor for compatibility
export const getSocket = () => socketClient.socket;

export default socketClient; 